/**
 * Thin fetch wrapper for the Accounts backend. Returns parsed JSON or
 * throws an `ApiError` on non-2xx responses. React Query turns these into
 * retry-or-surface decisions.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = {
    method,
    headers: { Accept: 'application/json' },
    credentials: 'include', // send the signed session cookie on every call
  };
  if (body !== undefined) {
    init.headers = { ...init.headers, 'Content-Type': 'application/json' };
    init.body = JSON.stringify(body);
  }
  const res = await fetch(path, init);
  const contentType = res.headers.get('content-type') ?? '';
  const parsed = contentType.includes('application/json')
    ? ((await res.json()) as unknown)
    : await res.text();
  if (!res.ok) {
    const message =
      typeof parsed === 'object' && parsed !== null && 'message' in parsed
        ? String((parsed as { message: unknown }).message)
        : `${method} ${path} failed with ${res.status}`;
    throw new ApiError(res.status, message, parsed);
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

// ---- Types mirroring the backend ---------------------------------------

export type PartyRole = 'self' | 'upline' | 'client';
export type PartyStatus = 'active' | 'archived';

export interface Party {
  id: number;
  role: PartyRole;
  displayName: string;
  notes: string | null;
  /**
   * When true, the rendered PDF/PNG ledger statement for this party
   * includes a Points column populated from settlements.client_points.
   * Default false; display-only.
   */
  showPoints: boolean;
  /** Binary archival state (migration 012). Archived parties are hidden from selection. */
  status: PartyStatus;
  createdAt: string;
  updatedAt: string;
}

/** One reference type blocking deletion (from GET /api/{parties,accounts}/:id/blockers). */
export interface BlockerCount {
  type: string;
  label: string;
  count: number;
}

export interface BlockerReport {
  blockers: BlockerCount[];
  total: number;
  deletable: boolean;
}

/** Render a blocker report as "3 assignments, 12 ledger entries". */
export function formatBlockerReport(r: BlockerReport): string {
  const plural = (label: string, n: number) =>
    n === 1
      ? label
      : label.endsWith('y')
        ? `${label.slice(0, -1)}ies`
        : label.endsWith('h')
          ? `${label}es`
          : `${label}s`;
  return r.blockers.map((b) => `${b.count} ${plural(b.label, b.count)}`).join(', ');
}

export interface Account {
  id: number;
  uplinePartyId: number;
  canonicalName: string;
  panel: string | null;
  currencyLabel: string;
  creditLimitLabel: string | null;
  status: 'active' | 'inactive' | 'closed';
  /** true = client rate mirrors upline rate; false = separate client rate. */
  equalSpread: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AccountAlias {
  id: number;
  accountId: number | null;
  rawName: string;
  ignored: boolean;
  ignoredReason: string | null;
}

/** One posted week touched by an alias's raw name (GET /api/aliases/:id/impact). */
export interface AliasImpactBatch {
  batchId: number;
  weekMonday: string;
  uplinePartyId: number;
  uplineName: string;
  rowCount: number;
}

export interface AliasImpact {
  alias: AccountAlias;
  postedBatches: AliasImpactBatch[];
}

// ---- Similar-name warnings (corrections Phase 4c) -------------------------
// POST /api/accounts and /api/parties answer 409 { error: 'SimilarName',
// details: { similar } } when a near-duplicate exists and the body lacks
// allowSimilar:true. Warn only — resubmit with allowSimilar to proceed.

export interface SimilarAccount {
  id: number;
  canonicalName: string;
  panel: string | null;
  status: 'active' | 'inactive' | 'closed';
}

export interface SimilarParty {
  id: number;
  displayName: string;
  status: PartyStatus;
}

/** Extract the similar-name list from a create-API 409, or null otherwise. */
export function similarFromError<T>(e: unknown): T[] | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  const body = e.body as
    | { error?: string; details?: { similar?: T[] } }
    | null
    | undefined;
  if (!body || body.error !== 'SimilarName') return null;
  return body.details?.similar ?? [];
}

export interface Assignment {
  id: number;
  accountId: number;
  clientPartyId: number | null;
  clientRate: string | null;
  uplineRate: string;
  defaultPointsSplit: boolean;
  effectiveFrom: string;
  effectiveTo: string | null;
  notes: string | null;
}

export interface AccountAssignments {
  assignments: Assignment[];
  activeAssignment: Assignment | null;
}

export interface RateChangeResponse {
  account: Account;
  assignment: Assignment;
}

export interface OpeningBalance {
  id: number;
  partyId: number;
  asOfDate: string;
  amount: string;
  note: string;
  source: string;
  createdAt: string;
}

export type WizardStep =
  | 'self'
  | 'uplines'
  | 'clients'
  | 'accounts'
  | 'opening_balances'
  | 'done';

export interface WizardState {
  completed: boolean;
  currentStep: WizardStep;
  stepStatus: Record<WizardStep, 'complete' | 'pending'>;
  counts: {
    selfParties: number;
    uplineParties: number;
    clientParties: number;
    accounts: number;
    assignments: number;
    aliases: number;
    openingBalances: number;
  };
}

export type BatchState = 'draft' | 'verified' | 'archived';

export interface WeeklyBatch {
  id: number;
  uplinePartyId: number;
  weekMonday: string;
  state: BatchState;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  verifiedAt: string | null;
  archivedAt: string | null;
}

export interface RawUplineRow {
  id: number;
  batchId: number;
  panel: string | null;
  rawAccountName: string;
  currencyLabelStated: string | null;
  creditLimitStated: string | null;
  pointsStated: string;
  rateStated: string;
  amountStated: string;
  flags: string[];
  sourceAttachmentId: number | null;
  sourceRowIndex: number | null;
  matchedAccountId: number | null;
  ignored: boolean;
  createdAt: string;
}

export type VarianceSeverity = 'NONE' | 'MINOR' | 'REVIEW' | 'MAJOR';
export type SettlementState = 'pending_confirmation' | 'computed' | 'locked';

export interface Settlement {
  id: number;
  batchId: number;
  accountId: number;
  assignmentId: number | null;
  rawRowId: number | null;
  clientPartyId: number | null;
  uplinePoints: string;
  clientPoints: string;
  uplineRateUsed: string;
  clientRateUsed: string | null;
  uplineCost: string;
  clientAmount: string | null;
  userMargin: string;
  amountStated: string | null;
  varianceDelta: string | null;
  varianceSeverity: VarianceSeverity;
  state: SettlementState;
  pointsOverridden: boolean;
  computedAt: string;
  lockedAt: string | null;
  notes: string | null;
}

export interface BatchTotals {
  uplineCostSum: string;
  clientAmountSum: string;
  userMarginSum: string;
  rowCount: number;
  majorVarianceCount: number;
  pendingCount: number;
}

export type AdvisoryKind =
  | 'rotation'
  | 'rate_drift'
  | 'no_assignment'
  | 'missing_raw'
  | 'duplicate_raw';

export interface SettlementAdvisory {
  settlementId: number;
  kind: AdvisoryKind;
  detail: string;
  previousBatchId?: number | null;
}

export interface BatchAttachment {
  id: number;
  batchId: number;
  sha256: string;
  originalFilename: string;
  mimeType: string;
  byteSize: number;
  uploadedAt: string;
}

export type VerifyBlocker =
  | { kind: 'UNMATCHED_ROW'; severity: 'warn'; rawRowId: number; detail: string }
  | { kind: 'PENDING_CONFIRMATION'; severity: 'block'; settlementId: number; detail: string }
  | { kind: 'MAJOR_VARIANCE'; severity: 'warn'; settlementId: number; detail: string };

export type VerifyBlockerKind = VerifyBlocker['kind'];

/**
 * Convenience: split a flat blockers list into hard blocks and soft warnings.
 * The Verify button is enabled iff `blocks.length === 0`; warnings require an
 * explicit acknowledgement modal but do not bar the transition.
 */
export function splitBySeverity(blockers: VerifyBlocker[]): {
  blocks: VerifyBlocker[];
  warns: VerifyBlocker[];
} {
  const blocks: VerifyBlocker[] = [];
  const warns: VerifyBlocker[] = [];
  for (const b of blockers) {
    if (b.severity === 'block') blocks.push(b);
    else warns.push(b);
  }
  return { blocks, warns };
}

// ---- Master-sheet paste import ------------------------------------------

export type SheetRowStatus =
  | 'READY'
  | 'UNRESOLVED_NAME'
  | 'NO_ASSIGNMENT'
  | 'CLIENT_MISMATCH'
  | 'RATE_MISMATCH'
  | 'ARITH_MISMATCH'
  | 'PARSE_ERROR';

export interface SheetFieldDiff {
  field: string;
  sheet: string;
  system: string;
}

export interface SheetVerifiedRow {
  rowIndex: number;
  status: SheetRowStatus;
  rawAccountName: string | null;
  poc: string | null;
  accountId?: number;
  accountName?: string;
  uplinePartyId?: number;
  clientPartyId?: number | null;
  clientPoints?: string;
  uplinePoints?: string;
  uplineRate?: string;
  uplineCost?: string;
  expectedClientPartyId?: number | null;
  expectedClientName?: string;
  pocUnresolved?: boolean;
  reason?: string;
  diffs?: SheetFieldDiff[];
}

export interface SheetBatchPlanEntry {
  uplinePartyId: number;
  uplineName: string;
  weekMonday: string;
  readyCount: number;
  batchState: 'none' | 'draft' | 'verified' | 'archived';
  batchId: number | null;
}

export interface SheetImportPreview {
  weekMonday: string;
  mapping: Record<string, number | undefined>;
  missingColumns: string[];
  rows: SheetVerifiedRow[];
  ready: SheetVerifiedRow[];
  exceptions: SheetVerifiedRow[];
  batchPlan: SheetBatchPlanEntry[];
}

export interface SheetImportApplyResult {
  batches: { batchId: number; uplinePartyId: number; uplineName: string; written: number }[];
  written: number;
  skippedExceptions: number;
}

export type RowClassification =
  | { kind: 'panel_header'; panel: string; rowIndex: number }
  | { kind: 'column_header'; rowIndex: number }
  | { kind: 'total'; rowIndex: number }
  | { kind: 'data'; rowIndex: number; panel: string | null }
  | { kind: 'empty'; rowIndex: number };

export interface PanelGroup {
  panel: string | null;
  dataRowIndexes: number[];
}

export interface ParsedSheet {
  name: string;
  rowCount: number;
  columnCount: number;
  rowOffset: number;
  rows: (string | null)[][];
  classifications: RowClassification[];
  panels: PanelGroup[];
}

export interface ParsedWorkbookResponse {
  originalFilename?: string | null;
  sheets: ParsedSheet[];
}

export interface OcrAccountMatch {
  rawName: string;
  accountId: number | null;
  accountLabel: string | null;
  via: 'alias' | 'fuzzy' | null;
}

export interface OcrKnownAccount {
  id: number;
  label: string;
  panel: string | null;
}

export interface OcrClassifyResponse extends ParsedWorkbookResponse {
  accountMatches: OcrAccountMatch[];
  knownAccounts: OcrKnownAccount[];
  /** True when vision-LLM failed and OCR fallback was used. */
  fallback?: boolean;
}

// ---- ledger / payments / adjustments ------------------------------------

export type LedgerEntryType = 'WEEKLY_PNL' | 'PAYMENT' | 'ADJUSTMENT' | 'TRANSFER';

export interface LedgerEntry {
  id: number;
  partyId: number;
  effectiveDate: string;
  type: LedgerEntryType;
  dr: string;
  cr: string;
  description: string;
  sourceSettlementId: number | null;
  sourcePaymentId: number | null;
  sourceAdjustmentId: number | null;
  sourceBatchId: number | null;
  reversesEntryId: number | null;
  postedAt: string;
}

export type PaymentMethod = 'cash' | 'bank_transfer' | 'upi' | 'cheque' | 'other';

export interface Payment {
  id: number;
  effectiveDate: string;
  amount: string;
  method: PaymentMethod;
  payerPartyId: number;
  payeePartyId: number;
  viaSelf: boolean;
  note: string | null;
  voidedAt: string | null;
  voidedReason: string | null;
  createdAt: string;
}

export interface Adjustment {
  id: number;
  effectiveDate: string;
  description: string;
  amount: string;
  drPartyId: number | null;
  crPartyId: number | null;
  note: string | null;
  voidedAt: string | null;
  voidedReason: string | null;
  createdAt: string;
}

export interface LedgerListingRow {
  id: number;
  effectiveDate: string;
  type: LedgerEntryType;
  dr: string;
  cr: string;
  description: string;
  sourceSettlementId: number | null;
  sourcePaymentId: number | null;
  sourceAdjustmentId: number | null;
  sourceBatchId: number | null;
  reversesEntryId: number | null;
  runningBalance: string;
}

export interface LedgerListing {
  partyId: number;
  from: string;
  to: string;
  openingBalanceAsOfFrom: string;
  rows: LedgerListingRow[];
  closingBalance: string;
}

export interface EntryAttachment {
  id: number;
  entryId: number;
  sha256: string;
  originalFilename: string;
  mimeType: string;
  byteSize: number;
  uploadedAt: string;
}

// ---- ledger-entry detail bundle ----------------------------------------

export interface PartyMini {
  id: number;
  displayName: string;
  role: string;
}

export interface AccountMini {
  id: number;
  canonicalName: string;
}

export interface RawUplineRowMini {
  id: number;
  batchId: number;
  panel: string | null;
  rawAccountName: string;
  pointsStated: string;
  rateStated: string;
  amountStated: string;
}

export type LedgerSource =
  | { kind: 'payment'; payment: Payment; bothLegs: LedgerEntry[] }
  | { kind: 'batch'; batch: WeeklyBatch; account?: AccountMini; rawRow?: RawUplineRowMini }
  | { kind: 'adjustment'; adjustment: Adjustment; bothLegs: LedgerEntry[] }
  | {
      kind: 'reversal';
      original: LedgerEntry;
      originalSource?: LedgerSource;
    }
  | { kind: 'none' };

export interface EntryDetail {
  entry: LedgerEntry;
  parties: Record<string, PartyMini>;
  source: LedgerSource;
  attachments: EntryAttachment[];
}

export interface PartyBalance {
  partyId: number;
  asOfDate: string | null;
  balance: string;
}

// ---- Reopen & Fix / rate drift (corrections Phase 3) ----------------------

/** Per-party preview of what unposting a batch would change right now. */
export interface PostingImpactParty {
  partyId: number;
  displayName: string;
  balanceBefore: string;
  delta: string;
  balanceAfter: string;
}

export interface PostingImpact {
  batchId: number;
  state: string;
  entryCount: number;
  parties: PostingImpactParty[];
}

/** A locked settlement whose snapshotted rate no longer matches the assignment covering its week. */
export interface RateDriftItem {
  batchId: number;
  weekMonday: string;
  uplinePartyId: number;
  uplineName: string;
  settlementId: number;
  accountId: number;
  accountName: string;
  uplineRateWas: string;
  uplineRateNow: string;
  clientRateWas: string | null;
  clientRateNow: string | null;
}

export interface RawRowReclassifyResult {
  row: RawUplineRow;
  recomputed: boolean;
}

// ---- Auth -----------------------------------------------------------------

export type UserRole = 'owner' | 'boss';

export interface UserSummary {
  id: number;
  username: string;
  role: UserRole;
  hasPin: boolean;
  createdAt: string;
}

export type WhoamiResponse =
  | { state: 'bootstrap_required' }
  | { state: 'unauthenticated' }
  | {
      state: 'authenticated';
      userId: number;
      username: string;
      role: UserRole;
      hasPin: boolean;
      pinUnlockedUntil: string | null;
    };

// ---- Match-entries module ------------------------------------------------

export type MatchAccountStatus = 'active' | 'archived';

export interface MatchAccount {
  id: number;
  name: string;
  notes: string | null;
  linkedPartyId: number | null;
  status: MatchAccountStatus;
  createdAt: string;
  updatedAt: string;
}

export interface MatchSplit {
  id: number;
  matchAccountId: number;
  targetMatchAccountId: number;
  label: string;
  percentage: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface MatchEntry {
  id: number;
  sourceMatchAccountId: number;
  amount: string;
  effectiveDate: string;
  note: string | null;
  createdByUserId: number;
  hadOverride: boolean;
  voidedAt: string | null;
  voidedReason: string | null;
  createdAt: string;
}

export interface MatchLedgerEntry {
  id: number;
  matchEntryId: number;
  targetMatchAccountId: number;
  amount: string;
  label: string | null;
  percentageUsed: string | null;
  partKind: 'split' | 'remainder';
  reversesEntryId: number | null;
  createdAt: string;
}

export interface MatchAccountDetail {
  account: MatchAccount;
  splits: MatchSplit[];
  balance: string;
}

export interface MatchEntryDetail {
  entry: MatchEntry;
  ledgerEntries: MatchLedgerEntry[];
}

export interface CreateMatchAccountInput {
  name: string;
  notes?: string | null;
  linkedPartyId?: number | null;
}

export interface CreateMatchEntryInput {
  sourceMatchAccountId: number;
  amount: string;
  effectiveDate: string;
  note?: string | null;
  overrideSplits?: Array<{
    targetMatchAccountId: number;
    label: string;
    percentage: string;
  }>;
}

export interface SplitReplaceInput {
  splits: Array<{
    targetMatchAccountId: number;
    label: string;
    percentage: string;
  }>;
}

export interface MatchLedgerRow {
  id: number;
  match_entry_id: number;
  target_match_account_id: number;
  amount: string;
  label: string | null;
  percentage_used: string | null;
  part_kind: 'split' | 'remainder';
  reverses_entry_id: number | null;
  created_at: string;
  effective_date: string;
  source_match_account_id: number;
  voided_at: string | null;
  note: string | null;
}

function buildQuery(
  params: Record<string, string | number | boolean | undefined | null>,
): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  if (entries.length === 0) return '';
  const qs = entries
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  return `?${qs}`;
}

export const match = {
  // ---- Accounts -----------------------------------------------------------
  listAccounts: (params?: { status?: 'active' | 'archived' | 'all' }) =>
    api.get<MatchAccount[]>(
      `/api/match/accounts${buildQuery({ status: params?.status })}`,
    ),
  getAccount: (id: number) =>
    api.get<MatchAccountDetail>(`/api/match/accounts/${id}`),
  createAccount: (input: CreateMatchAccountInput) =>
    api.post<MatchAccount>('/api/match/accounts', input),
  updateAccount: (id: number, input: Partial<CreateMatchAccountInput>) =>
    api.patch<MatchAccount>(`/api/match/accounts/${id}`, input),
  archiveAccount: (id: number) =>
    api.post<MatchAccount>(`/api/match/accounts/${id}/archive`),
  unarchiveAccount: (id: number) =>
    api.post<MatchAccount>(`/api/match/accounts/${id}/unarchive`),

  // ---- Splits -------------------------------------------------------------
  getSplits: (matchAccountId: number) =>
    api.get<MatchSplit[]>(`/api/match/accounts/${matchAccountId}/splits`),
  replaceSplits: (matchAccountId: number, input: SplitReplaceInput) =>
    api.put<MatchSplit[]>(
      `/api/match/accounts/${matchAccountId}/splits`,
      input,
    ),

  // ---- Entries ------------------------------------------------------------
  listEntries: (params?: {
    source?: number;
    from?: string;
    to?: string;
    includeVoided?: boolean;
    limit?: number;
    offset?: number;
  }) =>
    api.get<MatchEntry[]>(
      `/api/match/entries${buildQuery({
        source: params?.source,
        from: params?.from,
        to: params?.to,
        includeVoided: params?.includeVoided,
        limit: params?.limit,
        offset: params?.offset,
      })}`,
    ),
  getEntry: (id: number) =>
    api.get<MatchEntryDetail>(`/api/match/entries/${id}`),
  createEntry: (input: CreateMatchEntryInput) =>
    api.post<{ entry: MatchEntry; ledgerEntries: MatchLedgerEntry[] }>(
      '/api/match/entries',
      input,
    ),
  voidEntry: (id: number, reason: string) =>
    api.post<{ entry: MatchEntry; reversalEntries: MatchLedgerEntry[] }>(
      `/api/match/entries/${id}/void`,
      { reason },
    ),

  // ---- Ledger + balances --------------------------------------------------
  getLedger: (
    matchAccountId: number,
    params?: { from?: string; to?: string; limit?: number; offset?: number },
  ) =>
    api.get<{ rows: MatchLedgerRow[] }>(
      `/api/match/accounts/${matchAccountId}/ledger${buildQuery({
        from: params?.from,
        to: params?.to,
        limit: params?.limit,
        offset: params?.offset,
      })}`,
    ),
  getBalance: (matchAccountId: number) =>
    api.get<{ matchAccountId: number; balance: string }>(
      `/api/match/accounts/${matchAccountId}/balance`,
    ),
  listBalances: () =>
    api.get<Array<{ matchAccountId: number; balance: string }>>(
      '/api/match/balances',
    ),
};

// ---- Admin corrections (Fix Problems) -------------------------------------

export type CorrectionOpType = 'merge-account' | 'merge-party' | 'move-holder-history';

export interface MergeAccountParams {
  loserAccountId: number;
  survivorAccountId: number;
}

export interface MergePartyParams {
  loserPartyId: number;
  survivorPartyId: number;
}

export interface MoveHolderHistoryParams {
  accountId: number;
  fromPartyId: number;
  toPartyId: number;
  fromDate?: string;
  toDate?: string;
  archiveFromParty?: boolean;
}

export type CorrectionParams =
  | MergeAccountParams
  | MergePartyParams
  | MoveHolderHistoryParams;

export interface CorrectionBlockerInfo {
  code: string;
  message: string;
  remedy?: string;
}

export interface RepointChangeInfo {
  table: string;
  column: string;
  fromValue: number;
  toValue: number;
  rowIds: number[];
}

export interface CorrectionEntityAction {
  kind: 'attach-alias' | 'skip-alias' | 'delete-account' | 'archive-party';
  accountId?: number;
  partyId?: number;
  rawName?: string;
  reason?: string;
  beforeStatus?: string;
}

export interface CorrectionBalanceDelta {
  before: string;
  after: string;
  delta: string;
}

export interface CorrectionPlan {
  opType: CorrectionOpType;
  params: Record<string, unknown>;
  changeSet: RepointChangeInfo[];
  entityActions: CorrectionEntityAction[];
  blockers: CorrectionBlockerInfo[];
  balanceDeltas: Record<string, CorrectionBalanceDelta>;
  globalNetUnchanged: boolean;
  notes: string[];
  planHash: string;
}

export interface CorrectionHistoryRow {
  id: number;
  opType: CorrectionOpType;
  params: Record<string, unknown>;
  status: 'applied' | 'rolled_back';
  appliedAt: string;
  rolledBackAt: string | null;
  actor: string;
  notes: string | null;
  snapshotPath: string;
  /** merge-account only: the folded-away account's name (row is deleted). */
  loserAccountName: string | null;
  balanceDeltas: Record<string, CorrectionBalanceDelta>;
  rowsTouched: number;
  cleanlyUndoable: boolean;
  laterChanges: number;
}

export const corrections = {
  preview: (opType: CorrectionOpType, params: CorrectionParams) =>
    api.post<CorrectionPlan>('/api/corrections/preview', { opType, params }),
  apply: (opType: CorrectionOpType, params: CorrectionParams, planHash: string) =>
    api.post<{
      journalId: number;
      snapshotPath: string;
      planHash: string;
      balanceDeltas: Record<string, CorrectionBalanceDelta>;
    }>('/api/corrections/apply', { opType, params, planHash }),
  list: () =>
    api.get<{ corrections: CorrectionHistoryRow[] }>('/api/corrections'),
  undo: (id: number, force = false) =>
    api.post<{ ok: boolean; journalId: number }>(
      `/api/corrections/${id}/undo`,
      force ? { force: true } : {},
    ),
};

// ---- Cash Book module ----------------------------------------------------

export type CashAccountStatus = 'active' | 'archived';
export type CashDirection = 'in' | 'out';

export interface CashAccount {
  id: number;
  name: string;
  notes: string | null;
  openingBalance: string;
  linkedPartyId: number | null;
  status: CashAccountStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CashEntry {
  id: number;
  cashAccountId: number;
  direction: CashDirection;
  amount: string;
  effectiveDate: string;
  note: string | null;
  createdByUserId: number;
  reversesEntryId: number | null;
  createdAt: string;
}

export interface CashStatementRow {
  id: number;
  effectiveDate: string;
  direction: CashDirection;
  amount: string;
  note: string | null;
  reversesEntryId: number | null;
  runningBalance: string;
}

export interface CashStatement {
  cashAccountId: number;
  from: string;
  to: string;
  openingBalanceAsOfFrom: string;
  rows: CashStatementRow[];
  closingBalance: string;
}

export interface CashAccountDetail {
  account: CashAccount;
  balance: string;
}

export interface CashEntryAttachment {
  id: number;
  cashEntryId: number;
  sha256: string;
  originalFilename: string;
  mimeType: string;
  byteSize: number;
  uploadedAt: string;
}

export interface CreateCashAccountInput {
  name: string;
  notes?: string | null;
  openingBalance?: string | null;
  linkedPartyId?: number | null;
}

export interface CreateCashEntryInput {
  cashAccountId: number;
  direction: CashDirection;
  amount: string;
  effectiveDate: string;
  note?: string | null;
}

export const cash = {
  // ---- Accounts -----------------------------------------------------------
  listAccounts: (params?: { status?: CashAccountStatus | 'all' }) =>
    api.get<CashAccount[]>(
      `/api/cash/accounts${buildQuery({ status: params?.status })}`,
    ),
  getAccount: (id: number) =>
    api.get<CashAccountDetail>(`/api/cash/accounts/${id}`),
  createAccount: (input: CreateCashAccountInput) =>
    api.post<CashAccount>('/api/cash/accounts', input),
  updateAccount: (id: number, input: Partial<CreateCashAccountInput>) =>
    api.patch<CashAccount>(`/api/cash/accounts/${id}`, input),
  archiveAccount: (id: number) =>
    api.post<CashAccount>(`/api/cash/accounts/${id}/archive`),
  unarchiveAccount: (id: number) =>
    api.post<CashAccount>(`/api/cash/accounts/${id}/unarchive`),

  // ---- Balances -----------------------------------------------------------
  getBalance: (id: number) =>
    api.get<{ cashAccountId: number; balance: string }>(
      `/api/cash/accounts/${id}/balance`,
    ),
  listBalances: () =>
    api.get<Array<{ cashAccountId: number; balance: string }>>(
      '/api/cash/balances',
    ),

  // ---- Ledger -------------------------------------------------------------
  getLedger: (
    id: number,
    params: { from: string; to: string; includeReversed?: boolean },
  ) =>
    api.get<CashStatement>(
      `/api/cash/accounts/${id}/ledger${buildQuery({
        from: params.from,
        to: params.to,
        include_reversed: params.includeReversed ? 'true' : undefined,
      })}`,
    ),

  // ---- Entries ------------------------------------------------------------
  listEntries: (params?: { cashAccountId?: number; limit?: number }) =>
    api.get<CashEntry[]>(
      `/api/cash/entries${buildQuery({
        cashAccountId: params?.cashAccountId,
        limit: params?.limit,
      })}`,
    ),
  getEntry: (id: number) =>
    api.get<{ entry: CashEntry; attachments: CashEntryAttachment[] }>(
      `/api/cash/entries/${id}`,
    ),
  createEntry: (input: CreateCashEntryInput) =>
    api.post<CashEntry>('/api/cash/entries', input),
  voidEntry: (id: number, reason: string) =>
    api.post<{ original: CashEntry; reversal: CashEntry }>(
      `/api/cash/entries/${id}/void`,
      { reason },
    ),

  // ---- Attachments --------------------------------------------------------
  listAttachments: (entryId: number) =>
    api.get<CashEntryAttachment[]>(`/api/cash/entries/${entryId}/attachments`),
  deleteAttachment: (attachmentId: number) =>
    api.delete<void>(`/api/cash/entry-attachments/${attachmentId}`),
};
